import React, { useState, useEffect, useRef } from "react";

/**
 * DoozyBot 3D photorealistic mascot — ONE consistent body, never morphs.
 * All reactions are CSS-only overlays/transforms:
 *   - Blink: dark overlay briefly masks the LED eyes (~150ms)
 *   - Happy: gentle bounce
 *   - Surprised: quick scale pulse
 *   - Wave: left-right tilt wave
 * `pose` prop (null | "happy" | "surprised" | "wave") triggers a one-shot animation.
 */
const VERSION = "3d3";

const DoozyBotSVG = ({ size = 68, animate = true, pose = null, blinkWhileIdle = true }) => {
  const [isBlinking, setIsBlinking] = useState(false);
  const timersRef = useRef([]);

  // Preload once
  useEffect(() => {
    const img = new Image();
    img.src = `/doozybot/doozybot_idle.png?v=${VERSION}`;
  }, []);

  // Natural blink loop (only when idle, i.e., no explicit pose)
  useEffect(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current = [];
    setIsBlinking(false);

    if (!blinkWhileIdle || pose) return;

    let cancelled = false;
    const scheduleBlink = () => {
      if (cancelled) return;
      const wait = 3000 + Math.random() * 2000;
      const t1 = setTimeout(() => {
        if (cancelled) return;
        setIsBlinking(true);
        const t2 = setTimeout(() => {
          if (cancelled) return;
          setIsBlinking(false);
          if (Math.random() < 0.2) {
            const t3 = setTimeout(() => {
              if (cancelled) return;
              setIsBlinking(true);
              const t4 = setTimeout(() => {
                if (cancelled) return;
                setIsBlinking(false);
                scheduleBlink();
              }, 140);
              timersRef.current.push(t4);
            }, 180);
            timersRef.current.push(t3);
          } else {
            scheduleBlink();
          }
        }, 140);
        timersRef.current.push(t2);
      }, wait);
      timersRef.current.push(t1);
    };
    scheduleBlink();

    return () => {
      cancelled = true;
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current = [];
    };
  }, [blinkWhileIdle, pose]);

  // Re-trigger reaction animation on every pose change via key remount

  const reactionClass =
    pose === "happy" ? "db-react-happy" :
    pose === "surprised" ? "db-react-surprised" :
    pose === "wave" ? "db-react-wave" : "";

  return (
    <div style={{ width: size, height: size, position: "relative", display: "inline-block" }}>
      <style>{`
        @keyframes dbFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-3px) rotate(1deg); }
          50% { transform: translateY(-1px) rotate(-1deg); }
          75% { transform: translateY(-4px) rotate(0.5deg); }
        }
        @keyframes dbBreathe {
          0%, 100% { filter: drop-shadow(0 6px 14px rgba(0,0,0,0.28)); }
          50% { filter: drop-shadow(0 8px 18px rgba(0,0,0,0.33)); }
        }
        @keyframes dbBreatheScale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.025); }
        }
        @keyframes dbHappy {
          0%, 100% { transform: translateY(0) scale(1); }
          30% { transform: translateY(-6px) scale(1.05); }
          60% { transform: translateY(0) scale(1); }
          80% { transform: translateY(-3px) scale(1.02); }
        }
        @keyframes dbSurprised {
          0%, 100% { transform: scale(1); }
          20% { transform: scale(1.12) translateY(-2px); }
          50% { transform: scale(0.98); }
        }
        @keyframes dbWave {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-14deg); }
          40% { transform: rotate(12deg); }
          60% { transform: rotate(-10deg); }
          80% { transform: rotate(6deg); }
        }
        .db-wrap {
          width: 100%;
          height: 100%;
          position: relative;
          transform-origin: 50% 70%;
        }
        .db-wrap.anim { animation: ${animate ? "dbFloat 3.2s ease-in-out infinite, dbBreatheScale 4.2s ease-in-out infinite" : "none"}; }
        .db-react-happy { animation: dbHappy 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) !important; }
        .db-react-surprised { animation: dbSurprised 0.6s ease-out !important; }
        .db-react-wave { animation: dbWave 0.9s ease-in-out !important; transform-origin: 50% 90%; }
        .db-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
          filter: drop-shadow(0 6px 14px rgba(0,0,0,0.28));
          animation: ${animate ? "dbBreathe 4.2s ease-in-out infinite" : "none"};
          user-select: none;
          pointer-events: none;
        }
        .db-eyelid {
          position: absolute;
          background: #0a0a0f;
          border-radius: 50%;
          pointer-events: none;
          opacity: 0;
          transition: opacity 120ms ease-out;
          box-shadow: 0 0 2px #0a0a0f;
        }
        .db-eyelid.on { opacity: 1; }
        /* Eye positions calibrated to idle.png face bbox (25%-74% x, 14%-56% y).
           LED eyes sit roughly at 35%-48% and 52%-65% horizontally,
           y ~ 28%-36%. */
        .db-eyelid.left { left: 35%; top: 28%; width: 13%; height: 10%; }
        .db-eyelid.right { left: 52%; top: 28%; width: 13%; height: 10%; }
      `}</style>
      <div key={pose || "idle"} className={`db-wrap anim ${reactionClass}`}>
        <img
          src={`/doozybot/doozybot_idle.png?v=${VERSION}`}
          alt="DoozyBot"
          className="db-img"
          draggable={false}
        />
        <span className={`db-eyelid left ${isBlinking ? "on" : ""}`} />
        <span className={`db-eyelid right ${isBlinking ? "on" : ""}`} />
      </div>
    </div>
  );
};

export default DoozyBotSVG;
